/**
 * InboxPage — Unified message view with filter bar, message list, and detail panel.
 * Task 4.8
 */
import { useEffect, useState, useCallback } from "react";
import { Search, Inbox } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { EmptyState } from "@/components/ui/EmptyState";
import { MessageRow } from "@/components/inbox/MessageRow";
import { MessageDetail } from "@/components/inbox/MessageDetail";
import { useInboxStore } from "@/stores/useInboxStore";
import type { InboxMessage } from "@/types/inbox";

const channelOptions = [
  { value: "", label: "All Channels" },
  { value: "slack", label: "Slack" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "telegram", label: "Telegram" },
  { value: "whatsapp", label: "WhatsApp" },
];

const urgencyOptions = [
  { value: "", label: "All Urgency" },
  { value: "blocking", label: "Blocking" },
  { value: "clarification", label: "Clarification" },
  { value: "advisory", label: "Advisory" },
];

export function InboxPage() {
  const {
    messages,
    isLoading,
    filter,
    setFilter,
    loadMessages,
    markRead,
    reply,
  } = useInboxStore();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState(filter.search ?? "");

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const selectedMessage = messages.find((m) => m.id === selectedId) ?? null;

  const handleChannelFilter = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const next = { ...filter, channelType: e.target.value || undefined };
      setFilter(next);
      loadMessages(next);
    },
    [filter, setFilter, loadMessages],
  );

  const handleUrgencyFilter = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const next = { ...filter, urgency: e.target.value || undefined };
      setFilter(next);
      loadMessages(next);
    },
    [filter, setFilter, loadMessages],
  );

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const next = { ...filter, search: searchText || undefined };
      setFilter(next);
      loadMessages(next);
    },
    [filter, searchText, setFilter, loadMessages],
  );

  const handleSelect = useCallback(
    (msg: InboxMessage) => {
      setSelectedId(msg.id);
      if (!msg.isRead) {
        markRead(msg.id);
      }
    },
    [markRead],
  );

  const handleReply = useCallback(
    async (content: string) => {
      if (selectedId) {
        await reply(selectedId, content);
      }
    },
    [selectedId, reply],
  );

  const handleMarkRead = useCallback(() => {
    if (selectedId) {
      markRead(selectedId);
    }
  }, [selectedId, markRead]);

  return (
    <div className="flex h-full flex-col" data-testid="inbox-page">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4">
        <h1 className="text-2xl font-bold text-white">Inbox</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          All messages across your channels
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 border-b border-white/10 px-6 py-3" data-testid="filter-bar">
        <div className="w-40">
          <Select
            value={filter.channelType ?? ""}
            onChange={handleChannelFilter}
            aria-label="Filter by channel"
          >
            {channelOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-40">
          <Select
            value={filter.urgency ?? ""}
            onChange={handleUrgencyFilter}
            aria-label="Filter by urgency"
          >
            {urgencyOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>
        <form onSubmit={handleSearch} className="flex-1">
          <Input
            placeholder="Search messages…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            icon={<Search className="h-4 w-4" />}
          />
        </form>
      </div>

      {/* Content: message list + detail */}
      <div className="flex flex-1 overflow-hidden">
        {/* Message list (left panel) */}
        <div
          className="w-full overflow-y-auto border-r border-white/10 md:w-[60%]"
          data-testid="message-list"
        >
          {isLoading && messages.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          )}

          {!isLoading && messages.length === 0 && (
            <EmptyState
              icon={<Inbox className="h-6 w-6" />}
              title="No messages"
              description="Messages from your channels will appear here."
            />
          )}

          <div className="flex flex-col gap-1 p-2">
            {messages.map((msg) => (
              <MessageRow
                key={msg.id}
                message={msg}
                isSelected={msg.id === selectedId}
                onClick={() => handleSelect(msg)}
              />
            ))}
          </div>
        </div>

        {/* Detail panel (right) */}
        <div className="hidden flex-1 md:block" data-testid="detail-panel">
          {selectedMessage ? (
            <MessageDetail
              message={selectedMessage}
              onReply={handleReply}
              onMarkRead={handleMarkRead}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              <p className="text-sm">Select a message to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
